/**
 * TokenContext - 토큰 관리 전용 Context
 * 토큰 상태와 관련 기능만 분리하여 불필요한 리렌더링 방지
 */
import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { tokenManager as sharedTokenManager, isRefreshAuthFailure } from '../infrastructure/api/tokenManager';
import { AuthToken } from '../domains/auth/value-objects/AuthToken';
import { AuthLocalStorageAdapter } from '../domains/auth/repositories/AuthLocalStorageAdapter';

export interface TokenContextType {
  accessToken: string | null;
  refreshToken: string | null;
  isTokenValid: boolean;
  saveTokens: (access: string, refresh: string) => void;
  clearTokens: () => void;
  refreshAccessToken: () => Promise<string>;
}

const TokenContext = createContext<TokenContextType | undefined>(undefined);

export const useToken = (): TokenContextType => {
  const context = useContext(TokenContext);
  if (!context) {
    throw new Error('useToken must be used within a TokenProvider');
  }
  return context;
};

interface TokenProviderProps {
  children: ReactNode;
}

export const TokenProvider: React.FC<TokenProviderProps> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(() => 
    localStorage.getItem('accessToken')
  );
  const [refreshToken, setRefreshToken] = useState<string | null>(() => 
    localStorage.getItem('refreshToken')
  );

  // 토큰 저장
  const saveTokens = useCallback((access: string, refresh: string) => {
    setAccessToken(access);
    setRefreshToken(refresh);
    // Legacy 키에 저장
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
    // DDD 키에도 저장 (TokenService 호환성)
    try {
      const authToken = AuthToken.fromJWT(access, refresh);
      AuthLocalStorageAdapter.saveToken(authToken);
      AuthLocalStorageAdapter.updateLastActivity();
    } catch (e) {
      console.warn('⚠️ DDD 토큰 저장 실패:', e);
    }
  }, []);

  // 토큰 제거
  const clearTokens = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    // Legacy 키 삭제
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionId');
    // DDD 키도 삭제
    AuthLocalStorageAdapter.clearAll();
  }, []);

  // 토큰 갱신
  // 🔴 2026-09-24 우장산점 '매일 13시 로그아웃' 원인.
  //    종전엔 이 함수가 '페이지를 연 순간 읽어 둔' 리프레시 열쇠(React state)로 갱신했다.
  //    그런데 평소 갱신은 API 인터셉터(tokenManager)가 하면서 저장소의 열쇠를 새것으로 바꾼다.
  //    그러면 여기 state 의 열쇠는 이미 폐기된 옛것이 되고, 탭을 다시 볼 때 이 옛 열쇠로
  //    갱신 → 401 → clearTokens() → 모든 탭이 통째로 로그아웃됐다(운영 로그로 확인).
  //    이제 갱신은 한 곳(tokenManager)에서만 하고, 여기서는 결과를 state 에 옮겨 담기만 한다.
  const refreshAccessToken = useCallback(async (): Promise<string> => {
    try {
      const newAccessToken = await sharedTokenManager.refreshAccessToken();
      const newRefreshToken = sharedTokenManager.getRefreshToken();

      setAccessToken(newAccessToken);
      if (newRefreshToken) setRefreshToken(newRefreshToken);

      // DDD 키에도 저장 (TokenService 호환성)
      if (newRefreshToken) {
        try {
          const authToken = AuthToken.fromJWT(newAccessToken, newRefreshToken);
          AuthLocalStorageAdapter.saveToken(authToken);
          AuthLocalStorageAdapter.updateLastActivity();
        } catch (e) {
          console.warn('⚠️ DDD 토큰 저장 실패:', e);
        }
      }

      return newAccessToken;
    } catch (error) {
      console.error('❌ 토큰 갱신 실패:', error);
      // 서버가 열쇠를 거절했을 때만 로그아웃. 일시적 오류(네트워크·5xx)는 로그인 유지.
      if (isRefreshAuthFailure(error)) {
        clearTokens();
      }
      throw error;
    }
  }, [clearTokens]);

  // [2025-12-19] 중복 토큰 체크 제거
  // API 인터셉터에서 401 에러 시 자동으로 토큰 갱신하므로
  // 주기적인 토큰 체크는 불필요함 (중복 요청으로 인한 에러 발생 원인)
  // 토큰 갱신은 authApi.ts의 인터셉터에서만 담당

  // [2026-01-05] 탭 활성화 시 토큰 체크 및 갱신
  // 브라우저 탭을 오래 열어두면 토큰이 만료되어 로그아웃되는 문제 해결
  useEffect(() => {
    const handleVisibilityChange = async () => {
      // 탭이 다시 보이게 되었을 때만 실행
      if (document.visibilityState === 'visible') {
        console.log('🔍 [Tab Active] 탭 활성화 - 토큰 상태 확인');

        // DDD 방식으로 세션 유효성 검사
        const sessionStatus = AuthLocalStorageAdapter.validateSession();
        console.log('🔍 [Tab Active] 세션 상태:', sessionStatus);

        if (sessionStatus.needsRefresh) {
          // 토큰 만료됨 - 갱신 시도
          console.log('🔄 [Tab Active] 토큰 만료 - 갱신 시도');
          const canRefresh = AuthLocalStorageAdapter.canRefresh();

          if (canRefresh) {
            try {
              await refreshAccessToken();
              console.log('✅ [Tab Active] 토큰 갱신 성공');
              // state 동기화
              const newToken = localStorage.getItem('accessToken');
              const newRefresh = localStorage.getItem('refreshToken');
              if (newToken) setAccessToken(newToken);
              if (newRefresh) setRefreshToken(newRefresh);
            } catch (error) {
              console.warn('⚠️ [Tab Active] 토큰 갱신 실패:', error);
              // 갱신 실패해도 로그아웃하지 않음 - 다음 API 호출 시 처리
            }
          } else {
            console.warn('⚠️ [Tab Active] Refresh token 없음');
          }
        } else if (sessionStatus.isValid) {
          // 유효한 토큰 - 마지막 활동 시간 업데이트
          AuthLocalStorageAdapter.updateLastActivity();
        }
        // shouldLogout인 경우: 비활성 시간 초과로 이미 clearAll() 됨
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshAccessToken]);

  const value: TokenContextType = {
    accessToken,
    refreshToken,
    isTokenValid: !!accessToken,
    saveTokens,
    clearTokens,
    refreshAccessToken,
  };

  return <TokenContext.Provider value={value}>{children}</TokenContext.Provider>;
};